import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import ResultsHeader from './components/ResultsHeader';
import PerformanceMetrics from './components/PerformanceMetrics';
import ValidationResults from './components/ValidationResults';
import CodeAnalysis from './components/CodeAnalysis';
import ArtifactGallery from './components/ArtifactGallery';
import AchievementNotifications from './components/AchievementNotifications';
import NextStepsRecommendations from './components/NextStepsRecommendations';
import SocialSharing from './components/SocialSharing';

const MissionResultsSummary = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false); // Changed from true to false to prevent flash

  // Mock user data - immediately available to prevent loading flash
  const mockUser = {
    id: 1,
    username: "selenium_learner",
    email: "learner@seleniumsandbox.com",
    level: 3,
    totalXP: 1250,
    completedMissions: 12
  };

  useEffect(() => {
    // Immediately set user data without loading state to prevent homepage flash
    setUser(mockUser);
  }, []);

  // Mock mission result data
  const missionResult = {
    id: "mission_003",
    missionTitle: "Advanced Element Interactions",
    difficulty: "Intermediate",
    status: "passed", // passed, failed, partial
    score: 87,
    executionTime: "2m 34s",
    completedAt: "December 4, 2024 at 7:23 PM",
    socialStats: {
      shares: 23,
      githubCommits: 8,
      communityRank: "#142"
    }
  };

  // Mock performance metrics
  const performanceMetrics = {
    codeQuality: 85,
    executionSpeed: 78,
    testCoverage: 92,
    bestPractices: 81,
    overallScore: 87,
    actualTime: 154,
    testsRun: 11,
    totalTests: 12,
    practicesFollowed: 13,
    totalPractices: 16
  };

  // Mock validation results
  const validationResults = [
    {
      testName: "Login Form Automation",
      status: "passed",
      description: "Successfully automated login form with valid credentials",
      details: "All form fields were correctly identified and filled. Submit button clicked successfully.",
      screenshots: {
        expected: "https://images.unsplash.com/photo-1551650975-87deedd944c3?w=400&h=300&fit=crop",
        actual: "https://images.unsplash.com/photo-1551650975-87deedd944c3?w=400&h=300&fit=crop"
      }
    },
    {
      testName: "Dynamic Content Loading",
      status: "failed",
      description: "Failed to wait for dynamic content to load properly",
      details: "Element was not found within the specified timeout period. Consider using explicit waits.",
      suggestion: "Use WebDriverWait with expected_conditions for better reliability",
      screenshots: {
        expected: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop",
        actual: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&h=300&fit=crop"
      },
      differences: [
        "Expected element to be visible but was not found",
        "Timeout occurred after 10 seconds",
        "Page may require longer loading time"
      ]
    },
    {
      testName: "Form Validation Handling",
      status: "warning",
      description: "Form validation was handled but with minor issues",
      details: "Error messages were captured but formatting could be improved for better readability."
    }
  ];

  // Mock code analysis
  const codeAnalysis = {
    suggestions: [
      {
        title: "Use Explicit Waits Instead of Sleep",
        severity: "high",
        description: "Replace time.sleep() with WebDriverWait for more reliable automation",
        codeExample: `from selenium.webdriver.support.ui import WebDriverWait
from selenium.webdriver.support import expected_conditions as EC

wait = WebDriverWait(driver, 10)
element = wait.until(EC.element_to_be_clickable((By.ID, "submit-btn")))`,
        documentation: "https://selenium-python.readthedocs.io/waits.html"
      },
      {
        title: "Improve Element Locator Strategy",
        severity: "medium",
        description: "Consider using more robust locators like data attributes or CSS selectors",
        codeExample: `# Better approach
element = driver.find_element(By.CSS_SELECTOR, "[data-testid='login-button']")

# Instead of
element = driver.find_element(By.XPATH, "//button[contains(text(), 'Login')]")`,
        documentation: "https://selenium-python.readthedocs.io/locating-elements.html"
      }
    ],
    bestPractices: [
      {
        title: "Page Object Model Implementation",
        description: "Organize your code using the Page Object Model pattern for better maintainability",
        followed: true,
        recommendation: null
      },
      {
        title: "Exception Handling",
        description: "Implement proper exception handling for robust automation scripts",
        followed: false,
        recommendation: "Add try-catch blocks around critical operations to handle unexpected scenarios gracefully"
      },
      {
        title: "Browser Resource Management",
        description: "Properly close browser instances and clean up resources",
        followed: true,
        recommendation: null
      }
    ],
    patterns: [
      {
        name: "Fluent Interface Pattern",
        description: "Chain method calls for more readable and maintainable code",
        difficulty: "Intermediate",
        usage: "Common"
      },
      {
        name: "Factory Pattern for WebDriver",
        description: "Use factory pattern to create different browser instances",
        difficulty: "Advanced",
        usage: "Recommended"
      }
    ]
  };

  // Mock artifacts
  const artifacts = [
    {
      type: "screenshots",
      filename: "login_success.png",
      url: "https://images.unsplash.com/photo-1551650975-87deedd944c3?w=800&h=600&fit=crop",
      preview: "https://images.unsplash.com/photo-1551650975-87deedd944c3?w=200&h=150&fit=crop",
      size: 245760,
      timestamp: "2024-12-04 19:23:15",
      format: "PNG"
    },
    {
      type: "screenshots",
      filename: "form_validation_error.png",
      url: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=600&fit=crop",
      preview: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=200&h=150&fit=crop",
      size: 198432,
      timestamp: "2024-12-04 19:22:45",
      format: "PNG"
    },
    {
      type: "logs",
      filename: "execution_log.txt",
      url: "#",
      size: 15360,
      timestamp: "2024-12-04 19:25:30",
      format: "TXT",
      content: `[2024-12-04 19:20:15] INFO: Starting mission execution
[2024-12-04 19:20:16] INFO: Browser initialized successfully
[2024-12-04 19:20:17] INFO: Navigating to target URL
[2024-12-04 19:20:18] INFO: Page loaded successfully
[2024-12-04 19:20:19] INFO: Locating login form elements
[2024-12-04 19:20:20] INFO: Filling username field
[2024-12-04 19:20:21] INFO: Filling password field
[2024-12-04 19:20:22] INFO: Clicking submit button
[2024-12-04 19:20:24] INFO: Login successful
[2024-12-04 19:20:25] WARN: Dynamic content loading timeout
[2024-12-04 19:20:26] INFO: Retrying element location
[2024-12-04 19:20:28] INFO: Element found on retry
[2024-12-04 19:20:29] INFO: Mission completed with score: 87%`
    },
    {
      type: "reports",
      filename: "performance_report.json",
      url: "#",
      size: 8192,
      timestamp: "2024-12-04 19:25:35",
      format: "JSON"
    }
  ];

  // Mock achievements
  const achievements = [
    {
      id: "speed_demon",
      title: "Speed Demon",
      description: "Complete a mission in under 3 minutes",
      category: "speed",
      type: "gold",
      points: 100,
      isNew: true,
      earnedAt: "December 4, 2024",
      rarity: 15,
      progress: null
    },
    {
      id: "error_hunter",
      title: "Error Hunter",
      description: "Successfully handle 5 different types of exceptions",
      category: "quality",
      type: "silver",
      points: 75,
      isNew: true,
      earnedAt: "December 4, 2024",
      rarity: 28,
      progress: {
        current: 5,
        total: 5
      }
    },
    {
      id: "learning_streak",
      title: "Learning Streak",
      description: "Complete missions for 7 consecutive days",
      category: "streak",
      type: "milestone",
      points: 150,
      isNew: false,
      earnedAt: "December 4, 2024",
      rarity: 8,
      progress: {
        current: 7,
        total: 7
      },
      nextLevel: {
        title: "Master Streak",
        description: "Complete missions for 30 consecutive days"
      }
    }
  ];

  // Mock recommendations
  const recommendations = {
    primary: {
      id: "mission_004",
      title: "API Testing with Selenium",
      description: "Learn to combine Selenium with API testing for comprehensive web application testing",
      difficulty: "Intermediate",
      estimatedTime: "45 minutes",
      completionRate: 73,
      type: "next_mission",
      isUnlocked: true
    },
    others: [
      {
        id: "mission_review_002",
        title: "Form Handling Fundamentals",
        description: "Review basic form interactions and validation techniques",
        difficulty: "Beginner",
        estimatedTime: "30 minutes",
        type: "review",
        isUnlocked: true
      },
      {
        id: "challenge_001",
        title: "E-commerce Automation Challenge",
        description: "Advanced challenge involving complete e-commerce workflow automation",
        difficulty: "Advanced",
        estimatedTime: "90 minutes",
        type: "challenge",
        isUnlocked: false
      },
      {
        id: "skill_waits",
        title: "Mastering Selenium Waits",
        description: "Deep dive into implicit, explicit, and fluent waits",
        difficulty: "Intermediate",
        estimatedTime: "40 minutes",
        type: "skill_building",
        isUnlocked: true
      }
    ],
    learningPath: {
      id: "web_automation_path",
      name: "Web Automation Mastery",
      description: "Complete learning path covering all aspects of web automation testing",
      progress: {
        completed: 8,
        total: 15
      }
    },
    skillsToImprove: [
      { name: "Exception Handling", proficiency: 65 },
      { name: "Dynamic Content", proficiency: 45 },
      { name: "Performance Testing", proficiency: 30 }
    ]
  };

  const handleRetry = () => {
    navigate('/mission-workspace', { 
      state: { 
        missionId: missionResult?.id,
        retry: true 
      } 
    });
  };

  const handleNextMission = () => {
    navigate('/mission-workspace', { 
      state: { 
        missionId: recommendations?.primary?.id 
      } 
    });
  };

  const handleBackToMap = () => {
    navigate('/mission-map-dashboard');
  };

  const handleSelectMission = (mission) => {
    if (mission?.isUnlocked !== false) {
      navigate('/mission-workspace', { 
        state: { 
          missionId: mission?.id 
        } 
      });
    }
  };

  const handleViewLearningPath = (learningPath) => {
    navigate('/mission-map-dashboard', { 
      state: { 
        focusPath: learningPath?.id 
      } 
    });
  };

  const handleShareAchievement = (achievement) => {
    const shareText = `🏆 Just unlocked "${achievement?.title}" on Selenium Sandbox!\n\n${achievement?.description}\n\n+${achievement?.points} XP Points earned!\n\n#SeleniumAutomation #Achievement #WebTesting`;
    
    if (navigator.share) {
      navigator.share({
        title: 'Achievement Unlocked!',
        text: shareText,
        url: window.location?.href
      });
    } else {
      navigator.clipboard?.writeText(shareText);
    }
  };

  const handleGitHubCommit = async (missionResult, shareText) => {
    // Mock GitHub integration
    return new Promise((resolve) => {
      setTimeout(() => {
        console.log('GitHub commit created:', {
          mission: missionResult.missionTitle,
          score: missionResult.score,
          message: shareText
        });
        resolve();
      }, 1500);
    });
  };

  const handleLogout = () => {
    // Clear all authentication tokens and user data
    localStorage.removeItem('authToken');
    sessionStorage.removeItem('authToken');
    localStorage.removeItem('user');
    sessionStorage.removeItem('user');
    
    // Clear user state
    setUser(null);
    
    // Navigate to home page
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header 
        user={user} 
        onLogout={handleLogout}
      />
      <main className="content-offset">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="space-y-8">
            {/* Results Header */}
            <ResultsHeader
              missionResult={missionResult}
              onRetry={handleRetry}
              onNextMission={handleNextMission}
              onBackToMap={handleBackToMap}
            />

            {/* Performance Metrics */}
            <PerformanceMetrics metrics={performanceMetrics} />

            {/* Two Column Layout for Main Content */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
              {/* Left Column */}
              <div className="space-y-8">
                {/* Validation Results */}
                <ValidationResults validationResults={validationResults} />

                {/* Code Analysis */}
                <CodeAnalysis codeAnalysis={codeAnalysis} />
              </div>

              {/* Right Column */}
              <div className="space-y-8">
                {/* Achievement Notifications */}
                {achievements?.length > 0 && (
                  <AchievementNotifications
                    achievements={achievements}
                    onShare={handleShareAchievement}
                  />
                )}

                {/* Artifact Gallery */}
                <ArtifactGallery artifacts={artifacts} />
              </div>
            </div>

            {/* Full Width Sections */}
            <div className="space-y-8">
              {/* Next Steps Recommendations */}
              <NextStepsRecommendations
                recommendations={recommendations}
                onSelectMission={handleSelectMission}
                onViewLearningPath={handleViewLearningPath}
              />

              {/* Social Sharing */}
              <SocialSharing
                missionResult={missionResult}
                achievements={achievements}
                onGitHubCommit={handleGitHubCommit}
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MissionResultsSummary;